import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { getFirestoreInstance } from '../services/firebase';

type TutorialProgress = Record<string, string>;

type UseTutorialProgressReturn = {
  progress: TutorialProgress;
  loading: boolean;
  markWatched: (videoId: string) => Promise<void>;
};

export function useTutorialProgress(userEmail: string | null | undefined): UseTutorialProgressReturn {
  const [progress, setProgress] = useState<TutorialProgress>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userEmail) {
      setLoading(false);
      return;
    }

    async function fetchProgress() {
      try {
        const db = await getFirestoreInstance();
        const progressRef = doc(db, 'tutorialProgress', userEmail!);
        const snap = await getDoc(progressRef);
        if (snap.exists()) {
          setProgress(snap.data() as TutorialProgress);
        }
      } catch (err) {
        console.error('Failed to fetch tutorial progress:', err);
      } finally {
        setLoading(false);
      }
    }

    void fetchProgress();
  }, [userEmail]);

  const markWatched = useCallback(async (videoId: string) => {
    if (!userEmail) return;

    const now = new Date().toISOString();
    const updated = { ...progress, [videoId]: now };
    setProgress(updated);

    try {
      const db = await getFirestoreInstance();
      const progressRef = doc(db, 'tutorialProgress', userEmail);
      await setDoc(progressRef, updated, { merge: true });
    } catch (err) {
      console.error('Failed to save tutorial progress:', err);
    }
  }, [userEmail, progress]);

  return { progress, loading, markWatched };
}
